import * as React from 'react'
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { ManageForecastGroupNamesList } from './-components/ManageForecastGroupNamesList'
import { PageTitle } from '@/components/PageTitle'
import { Department } from '@/types/user.types'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { useLdSettled } from '@/hooks/use-ld-settled'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

/**
 * Backoffice "Forecast group names" (sc-608 round 13) — admin management of
 * the forecast-group name suggestion pool `GroupNamePopover` offers every
 * planner. Modeled on the Labels page (`../labels/index.tsx`): same
 * ADMIN-only route guard, breadcrumb, and layout.
 *
 * Gated by the `interactiveEscapePlan` LaunchDarkly flag on top of the
 * ADMIN department floor — see `RouteComponent`'s flag check below for why
 * that can't live in `beforeLoad` alongside the department check.
 */
export const Route = createFileRoute('/_app/backoffice/forecast-group-names/')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const { isAuthenticated, isLoading, departmentResolved, user } =
      context.auth
    if (!isAuthenticated && !isLoading) {
      throw redirect({ to: '/login' })
    }
    // Only redirect once the department is actually resolved and known to be
    // non-admin — checking too early bounces admins to /home on a cold
    // refresh/deep-link (department loads from a backend fetch after Keycloak
    // init). While unresolved, the server-side @RequireAdmin guard enforces access.
    if (departmentResolved && user && user.department !== Department.ADMIN) {
      throw redirect({ to: '/home' })
    }
  },
})

function RouteComponent() {
  const { interactiveEscapePlan } = useFeatureFlags()
  const ldSettled = useLdSettled()
  const navigate = useNavigate()
  const flagOff = ldSettled && !interactiveEscapePlan

  // The flag cannot be checked in `beforeLoad` alongside the department guard
  // above: LaunchDarkly only resolves in the browser, so there is nothing to
  // read when that guard runs. Hence a component-level gate here, same
  // pattern as the Learning area's layout route (`_app/learning/route.tsx`)
  // — except this redirects back to `/backoffice`, the hub this page's tile
  // lives on, rather than `/home`.
  React.useEffect(() => {
    if (flagOff) void navigate({ to: '/backoffice', replace: true })
  }, [flagOff, navigate])

  // Hold the spinner through both the unsettled window and the redirect, so
  // the page never flashes content it is about to navigate away from.
  if (!ldSettled || flagOff) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="py-4">
      <PageTitle title="Forecast group names" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Forecast group names</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-4">
        <h1 className="text-3xl font-bold tracking-tight">
          Forecast group names
        </h1>
        <p className="text-muted-foreground mt-2">
          Names planners can pick for a client&apos;s forecast groups. Suggested
          names are offered to every planner first; names planners create show
          up here too. Renaming or deleting a name changes it on every forecast
          group that uses it.
        </p>
      </div>

      <ManageForecastGroupNamesList />
    </div>
  )
}
